/**
 * CoachPlaybook - ShotChart Component
 * Version: 0.5.145
 * Description: Visual shot chart for tracking makes/misses on court
 * 
 * Features:
 * - Tap player, tap court location, mark make/miss
 * - Green X for makes, Red O for misses
 * - View by player, position, team
 * - Switch between periods (quarters/halves)
 * - Real-time FG% calculation
 */

import React, { useState, useRef, useEffect } from 'react';
import JerseyIcon, { DEFAULT_JERSEY_TEMPLATE } from '../common/JerseyIcon';

// Shot data structure
const DEFAULT_SHOT = {
  id: '',
  playerId: '',
  x: 0,           // % from left
  y: 0,           // % from top
  made: false,
  period: 1,      // 1-4 for quarters, 1-2 for halves
  shotType: '2pt', // '2pt', '3pt', 'ft'
  timestamp: null
};

const ShotChart = ({ 
  game,
  players = [],
  jerseyTemplates = [],
  activeTemplateId,
  onShotsUpdate,
  onClose
}) => {
  const [shots, setShots] = useState(game?.shots || []);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState(1);
  const [periodType, setPeriodType] = useState('quarters'); // 'quarters' or 'halves'
  const [viewFilter, setViewFilter] = useState('all'); // 'all', 'made', 'missed'
  const [playerFilter, setPlayerFilter] = useState('all'); // 'all', playerId, or position
  const [isPlacingShot, setIsPlacingShot] = useState(false);
  const [pendingShot, setPendingShot] = useState(null); // { x, y } waiting for make/miss
  const [deletingShotId, setDeletingShotId] = useState(null); // Inline confirm for shot deletion
  const courtRef = useRef(null);

  // Get jersey template for player - prefer isDefault
  const getPlayerTemplate = (player) => {
    if (!player) return DEFAULT_JERSEY_TEMPLATE;
    
    // If player has specific template assigned
    if (player.jerseyTemplateId && jerseyTemplates.length > 0) {
      const assigned = jerseyTemplates.find(t => t.id === player.jerseyTemplateId);
      if (assigned) return assigned;
    }
    
    // Prefer default jersey (isDefault), then activeTemplateId, then first
    if (jerseyTemplates.length > 0) {
      const defaultJersey = jerseyTemplates.find(t => t.isDefault);
      if (defaultJersey) return defaultJersey;
      
      if (activeTemplateId) {
        const active = jerseyTemplates.find(t => t.id === activeTemplateId);
        if (active) return active;
      }
      
      return jerseyTemplates[0];
    }
    
    return DEFAULT_JERSEY_TEMPLATE;
  };

  // Calculate if shot is a 3-pointer based on position
  const is3Pointer = (x, y) => {
    // Court dimensions: 3pt line is roughly at 23.75 ft from basket
    // In our percentage-based system, we'll approximate
    // Top arc: y < 25% or y > 75%
    // Corner 3s: x < 15% or x > 85% when in certain y range
    const centerX = 50;
    const basketY = 10; // Basket is near top
    
    // Distance from basket (rough approximation)
    const dx = Math.abs(x - centerX);
    const dy = Math.abs(y - basketY);
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // 3pt line is roughly at distance 40 in our % system
    return distance > 38 || (y > 15 && (x < 8 || x > 92));
  };

  // Handle court tap
  const handleCourtTap = (e) => {
    if (!selectedPlayerId) {
      alert('Select a player first');
      return;
    }

    const rect = courtRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Set pending shot location
    setPendingShot({ x, y });
    setIsPlacingShot(true);
  };

  // Confirm shot as make or miss
  const confirmShot = (made) => {
    if (!pendingShot || !selectedPlayerId) return;

    const shotType = is3Pointer(pendingShot.x, pendingShot.y) ? '3pt' : '2pt';
    
    const newShot = {
      id: `shot_${Date.now()}`,
      playerId: selectedPlayerId,
      x: pendingShot.x,
      y: pendingShot.y,
      made,
      period: currentPeriod,
      shotType,
      timestamp: new Date().toISOString()
    };

    const updatedShots = [...shots, newShot];
    setShots(updatedShots);
    
    if (onShotsUpdate) {
      onShotsUpdate(updatedShots);
    }

    setPendingShot(null);
    setIsPlacingShot(false);
  };

  // Cancel pending shot
  const cancelShot = () => {
    setPendingShot(null);
    setIsPlacingShot(false);
  };

  // Delete a shot
  const deleteShot = (shotId) => {
    const updatedShots = shots.filter(s => s.id !== shotId);
    setShots(updatedShots);
    if (onShotsUpdate) {
      onShotsUpdate(updatedShots);
    }
  };

  // Get filtered shots for display
  const getFilteredShots = () => {
    let filtered = shots;

    // Filter by period (skip filter when viewing all periods)
    if (currentPeriod !== 'all') {
      filtered = filtered.filter(s => s.period === currentPeriod);
    }

    // Filter by made/missed
    if (viewFilter === 'made') {
      filtered = filtered.filter(s => s.made);
    } else if (viewFilter === 'missed') {
      filtered = filtered.filter(s => !s.made);
    }

    // Filter by player or position
    if (playerFilter !== 'all') {
      if (['PG', 'SG', 'SF', 'PF', 'C'].includes(playerFilter)) {
        const positionPlayerIds = players.filter(p => p.position === playerFilter).map(p => p.id);
        filtered = filtered.filter(s => positionPlayerIds.includes(s.playerId));
      } else {
        filtered = filtered.filter(s => s.playerId === playerFilter);
      }
    }

    return filtered;
  };

  // Calculate stats
  const getStats = (playerId = null) => {
    let relevantShots = shots;
    
    if (playerId) {
      relevantShots = shots.filter(s => s.playerId === playerId);
    }

    const periodShots = currentPeriod === 'all' ? relevantShots : relevantShots.filter(s => s.period === currentPeriod);
    const allPeriodShots = relevantShots;

    // Current period stats
    const periodMade = periodShots.filter(s => s.made).length;
    const periodTotal = periodShots.length;
    const period2pt = periodShots.filter(s => s.shotType === '2pt');
    const period3pt = periodShots.filter(s => s.shotType === '3pt');

    // All periods stats
    const totalMade = allPeriodShots.filter(s => s.made).length;
    const totalAttempts = allPeriodShots.length;
    const total2pt = allPeriodShots.filter(s => s.shotType === '2pt');
    const total3pt = allPeriodShots.filter(s => s.shotType === '3pt');

    return {
      period: {
        made: periodMade,
        total: periodTotal,
        pct: periodTotal > 0 ? ((periodMade / periodTotal) * 100).toFixed(1) : '0.0',
        fg2Made: period2pt.filter(s => s.made).length,
        fg2Total: period2pt.length,
        fg3Made: period3pt.filter(s => s.made).length,
        fg3Total: period3pt.length
      },
      game: {
        made: totalMade,
        total: totalAttempts,
        pct: totalAttempts > 0 ? ((totalMade / totalAttempts) * 100).toFixed(1) : '0.0',
        fg2Made: total2pt.filter(s => s.made).length,
        fg2Total: total2pt.length,
        fg3Made: total3pt.filter(s => s.made).length,
        fg3Total: total3pt.length,
        points: (total2pt.filter(s => s.made).length * 2) + (total3pt.filter(s => s.made).length * 3)
      }
    };
  };

  const filteredShots = getFilteredShots();
  const selectedPlayer = players.find(p => p.id === selectedPlayerId);
  const stats = getStats(playerFilter !== 'all' && !['PG', 'SG', 'SF', 'PF', 'C'].includes(playerFilter) ? playerFilter : null);
  const periods = periodType === 'quarters' ? [1, 2, 3, 4] : [1, 2];

  return (
    <div className="fixed inset-0 bg-slate-900 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-bold text-white">Shot Chart</h1>
            <p className="text-sm text-slate-400">vs {game?.opponent || 'Opponent'}</p>
          </div>
        </div>
        
        {/* Period Toggle */}
        <div className="flex items-center gap-2">
          <select 
            value={periodType}
            onChange={(e) => {
              setPeriodType(e.target.value);
              setCurrentPeriod(1);
            }}
            className="bg-slate-700 text-white text-sm rounded px-2 py-1 border border-slate-600"
          >
            <option value="quarters">Quarters</option>
            <option value="halves">Halves</option>
          </select>
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-800/50">
        {periods.map(p => (
          <button
            key={p}
            onClick={() => setCurrentPeriod(p)}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              currentPeriod === p 
                ? 'bg-blue-600 text-white' 
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {periodType === 'quarters' ? `Q${p}` : `H${p}`}
          </button>
        ))}
        <button
          onClick={() => setCurrentPeriod('all')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            currentPeriod === 'all' 
              ? 'bg-blue-600 text-white' 
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          All
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Court Area */}
        <div className="flex-1 flex flex-col p-4">
          {/* Stats Bar */}
          <div className="flex items-center justify-between mb-3 px-2">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-black text-white">{stats.period.made}/{stats.period.total}</div>
                <div className="text-xs text-slate-400">Period FG</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black text-green-400">{stats.period.pct}%</div>
                <div className="text-xs text-slate-400">Period FG%</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-lg font-bold text-white">{stats.game.fg2Made}/{stats.game.fg2Total}</div>
                <div className="text-xs text-slate-400">2PT</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-white">{stats.game.fg3Made}/{stats.game.fg3Total}</div>
                <div className="text-xs text-slate-400">3PT</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-black text-yellow-400">{stats.game.points}</div>
                <div className="text-xs text-slate-400">PTS</div>
              </div>
            </div>
          </div>

          {/* Court */}
          <div 
            ref={courtRef}
            onClick={handleCourtTap}
            className="relative flex-1 bg-amber-700 rounded-lg overflow-hidden cursor-crosshair border-4 border-white"
            style={{ 
              backgroundImage: 'url(/court-half.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              minHeight: '300px',
              maxHeight: '500px'
            }}
          >
            {/* Shot Markers */}
            {filteredShots.map(shot => {
              const player = players.find(p => p.id === shot.playerId);
              return (
                <div
                  key={shot.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
                  style={{ left: `${shot.x}%`, top: `${shot.y}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeletingShotId(deletingShotId === shot.id ? null : shot.id);
                  }}
                >
                  {shot.made ? (
                    // Made shot - Green X
                    <svg className="w-6 h-6 text-green-500 drop-shadow-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4}>
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  ) : (
                    // Missed shot - Red O
                    <svg className="w-6 h-6 text-red-500 drop-shadow-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4}>
                      <circle cx="12" cy="12" r="8" />
                    </svg>
                  )}
                  {/* Hover tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-black/80 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    #{player?.jerseyNumber} - {shot.shotType.toUpperCase()}
                  </div>
                  {/* Inline delete confirm */}
                  {deletingShotId === shot.id && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 flex items-center gap-1 bg-slate-800 border border-slate-600 rounded-lg px-2 py-1.5 whitespace-nowrap z-10 shadow-lg" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-red-400 mr-1">Delete?</span>
                      <button onClick={(e) => { e.stopPropagation(); setDeletingShotId(null); }} className="px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600">No</button>
                      <button onClick={(e) => { e.stopPropagation(); deleteShot(shot.id); setDeletingShotId(null); }} className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-500">Yes</button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pending Shot Marker */}
            {pendingShot && (
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${pendingShot.x}%`, top: `${pendingShot.y}%` }}
              >
                <div className="w-8 h-8 bg-white rounded-full border-4 border-blue-500 animate-pulse" />
              </div>
            )}

            {/* Legend */}
            <div className="absolute bottom-2 left-2 flex items-center gap-3 bg-black/60 px-3 py-1.5 rounded-lg">
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4}>
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
                <span className="text-xs text-white">Make</span>
              </div>
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4}>
                  <circle cx="12" cy="12" r="8" />
                </svg>
                <span className="text-xs text-white">Miss</span>
              </div>
            </div>
          </div>

          {/* Make/Miss Buttons (shown when placing shot) */}
          {isPlacingShot && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <button
                onClick={() => confirmShot(true)}
                className="flex-1 max-w-[150px] py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-500 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4}>
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
                MAKE
              </button>
              <button
                onClick={() => confirmShot(false)}
                className="flex-1 max-w-[150px] py-4 bg-red-600 text-white rounded-xl font-bold text-lg hover:bg-red-500 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4}>
                  <circle cx="12" cy="12" r="8" />
                </svg>
                MISS
              </button>
              <button
                onClick={cancelShot}
                className="px-4 py-4 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Player Selector Sidebar */}
        <div className="w-full lg:w-72 bg-slate-800 border-t lg:border-t-0 lg:border-l border-slate-700 flex flex-col">
          {/* Selected Player Display */}
          <div className="p-4 border-b border-slate-700">
            <h3 className="text-sm font-semibold text-slate-400 mb-2">SELECTED PLAYER</h3>
            {selectedPlayer ? (
              <div className="flex items-center gap-3 p-3 bg-blue-600/20 border-2 border-blue-500 rounded-xl">
                <JerseyIcon 
                  size={40} 
                  number={selectedPlayer.jerseyNumber}
                  {...getPlayerTemplate(selectedPlayer)}
                />
                <div>
                  <div className="text-white font-bold">{selectedPlayer.firstName} {selectedPlayer.lastName}</div>
                  <div className="text-blue-300 text-sm">#{selectedPlayer.jerseyNumber} • {selectedPlayer.position}</div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-slate-700/50 rounded-xl text-center text-slate-400">
                Tap a player to select
              </div>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex border-b border-slate-700">
            <button
              onClick={() => setPlayerFilter('all')}
              className={`flex-1 py-2 text-sm font-medium ${playerFilter === 'all' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-400'}`}
            >
              All
            </button>
            <button
              onClick={() => setViewFilter(viewFilter === 'made' ? 'all' : 'made')}
              className={`flex-1 py-2 text-sm font-medium ${viewFilter === 'made' ? 'text-green-400 border-b-2 border-green-400' : 'text-slate-400'}`}
            >
              Made
            </button>
            <button
              onClick={() => setViewFilter(viewFilter === 'missed' ? 'all' : 'missed')}
              className={`flex-1 py-2 text-sm font-medium ${viewFilter === 'missed' ? 'text-red-400 border-b-2 border-red-400' : 'text-slate-400'}`}
            >
              Missed
            </button>
          </div>

          {/* Player List */}
          <div className="flex-1 overflow-y-auto p-2">
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
              {players.map(player => {
                const template = getPlayerTemplate(player);
                const playerStats = getStats(player.id);
                const isSelected = selectedPlayerId === player.id;
                
                return (
                  <button
                    key={player.id}
                    onClick={() => setSelectedPlayerId(player.id)}
                    className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                      isSelected 
                        ? 'bg-blue-600 ring-2 ring-blue-400' 
                        : 'bg-slate-700 hover:bg-slate-600'
                    }`}
                  >
                    <JerseyIcon 
                      size={36} 
                      number={player.jerseyNumber}
                      fillColor={template.fillColor}
                      outlineColor={template.outlineColor}
                      numberColor={template.numberColor}
                      numberOutline={template.numberOutline}
                    />
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-white font-medium text-sm truncate">
                        {player.lastName}
                      </div>
                      <div className="text-slate-400 text-xs">
                        {playerStats.game.made}/{playerStats.game.total} FG
                      </div>
                    </div>
                    {playerStats.game.total > 0 && (
                      <div className={`text-sm font-bold ${
                        parseFloat(playerStats.game.pct) >= 50 ? 'text-green-400' : 
                        parseFloat(playerStats.game.pct) >= 30 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {playerStats.game.pct}%
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Position Filter */}
          <div className="p-3 border-t border-slate-700">
            <div className="flex gap-1">
              {['PG', 'SG', 'SF', 'PF', 'C'].map(pos => (
                <button
                  key={pos}
                  onClick={() => setPlayerFilter(playerFilter === pos ? 'all' : pos)}
                  className={`flex-1 py-1.5 text-xs font-medium rounded ${
                    playerFilter === pos 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                  }`}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShotChart;
